import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

export const ToasterContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10000;
  animation: ${slideIn} 0.3s ease-out;
  min-width: 540px;
  max-width: 600px;
  background: ${(props) =>
    props.theme?.name === 'dark' ? '#1E293B' : '#FFFFFF'};
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid;

  @media (max-width: 768px) {
    min-width: unset;
    max-width: calc(100vw - 32px);
    width: calc(100vw - 32px);
    bottom: 16px;
    right: 16px;
    left: 16px;
  }

  ${(props) =>
    props.type === 'success' &&
    `
    border-color: ${
      props.theme?.name === 'dark'
        ? 'rgba(34, 197, 94, 0.5)'
        : 'rgba(34, 197, 94, 0.3)'
    };
    box-shadow: 
      0 0 40px ${
        props.theme?.name === 'dark'
          ? 'rgba(34, 197, 94, 0.4)'
          : 'rgba(34, 197, 94, 0.2)'
      }, 
      0 30px 60px rgba(0, 0, 0, ${
        props.theme?.name === 'dark' ? '0.5' : '0.15'
      });
  `}

  ${(props) =>
    props.type === 'error' &&
    `
    border-color: ${
      props.theme?.name === 'dark'
        ? 'rgba(239, 68, 68, 0.5)'
        : 'rgba(239, 68, 68, 0.3)'
    };
    box-shadow: 
      0 0 40px ${
        props.theme?.name === 'dark'
          ? 'rgba(239, 68, 68, 0.4)'
          : 'rgba(239, 68, 68, 0.2)'
      },
      0 30px 60px rgba(0, 0, 0, ${
        props.theme?.name === 'dark' ? '0.5' : '0.15'
      });
  `}
  
  ${(props) =>
    props.type === 'info' &&
    `
    border-color: ${
      props.theme?.name === 'dark'
        ? 'rgba(59, 130, 246, 0.5)'
        : 'rgba(59, 130, 246, 0.3)'
    };
    box-shadow: 
      0 0 40px ${
        props.theme?.name === 'dark'
          ? 'rgba(59, 130, 246, 0.4)'
          : 'rgba(59, 130, 246, 0.2)'
      },
      0 30px 60px rgba(0, 0, 0, ${
        props.theme?.name === 'dark' ? '0.5' : '0.15'
      });
  `}
`;

export const ToasterMainContent = styled.div`
  display: flex;
  align-items: stretch;
`;

export const ToasterIconSection = styled.div`
  width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 60px;
  }

  ${(props) =>
    props.type === 'success' &&
    `
    background: linear-gradient(135deg, ${
      props.theme?.colors?.status?.success || '#22C55E'
    }, #16A34A);
  `}

  ${(props) =>
    props.type === 'error' &&
    `
    background: linear-gradient(135deg, ${
      props.theme?.colors?.status?.error || '#EF4444'
    }, ${props.theme?.colors?.status?.errorAlt || '#DC2626'});
  `}
  
  ${(props) =>
    props.type === 'info' &&
    `
    background: linear-gradient(135deg, ${
      props.theme?.colors?.status?.info || '#3B82F6'
    }, #2563EB);
  `}
  
  svg {
    width: 48px;
    height: 48px;
    color: white;

    @media (max-width: 768px) {
      width: 32px;
      height: 32px;
    }
  }
`;

export const ToasterContentSection = styled.div`
  padding: ${(props) => (props.hasAction ? '20px 24px' : '28px 32px')};
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 768px) {
    padding: ${(props) => (props.hasAction ? '16px' : '16px 20px')};
    gap: 12px;
  }
`;

export const ToasterContent = styled.div`
  flex: 1;
`;

export const ToasterTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => (props.theme?.name === 'dark' ? '#F9FAFB' : '#0F172A')};
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 4px;
  }
`;

export const ToasterMessage = styled.div`
  font-size: 16px;
  color: ${(props) =>
    props.theme?.name === 'dark'
      ? props.theme?.colors?.text?.secondaryMuted || '#94A3B8'
      : '#64748B'};
  line-height: 1.5;
  margin-bottom: ${(props) => (props.hasLink ? '8px' : '0')};

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
  }
`;

export const ToasterActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const ToasterActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  ${(props) =>
    props.type === 'success' &&
    `
    background: ${
      props.theme?.name === 'dark'
        ? 'rgba(34, 197, 94, 0.2)'
        : 'rgba(34, 197, 94, 0.1)'
    };
    color: ${props.theme?.colors?.status?.success || '#22C55E'};
    border: 1px solid ${
      props.theme?.name === 'dark'
        ? 'rgba(34, 197, 94, 0.3)'
        : 'rgba(34, 197, 94, 0.2)'
    };
    
    &:hover {
      background: ${
        props.theme?.name === 'dark'
          ? 'rgba(34, 197, 94, 0.3)'
          : 'rgba(34, 197, 94, 0.15)'
      };
    }
  `}

  ${(props) =>
    props.type === 'error' &&
    `
    background: ${
      props.theme?.name === 'dark'
        ? 'rgba(239, 68, 68, 0.2)'
        : 'rgba(239, 68, 68, 0.1)'
    };
    color: ${props.theme?.colors?.status?.error || '#EF4444'};
    border: 1px solid ${
      props.theme?.name === 'dark'
        ? 'rgba(239, 68, 68, 0.3)'
        : 'rgba(239, 68, 68, 0.2)'
    };
    
    &:hover {
      background: ${
        props.theme?.name === 'dark'
          ? 'rgba(239, 68, 68, 0.3)'
          : 'rgba(239, 68, 68, 0.15)'
      };
    }
  `}
  
  ${(props) =>
    props.type === 'info' &&
    `
    background: ${
      props.theme?.name === 'dark'
        ? 'rgba(59, 130, 246, 0.2)'
        : 'rgba(59, 130, 246, 0.1)'
    };
    color: ${props.theme?.colors?.status?.info || '#3B82F6'};
    border: 1px solid ${
      props.theme?.name === 'dark'
        ? 'rgba(59, 130, 246, 0.3)'
        : 'rgba(59, 130, 246, 0.2)'
    };
    
    &:hover {
      background: ${
        props.theme?.name === 'dark'
          ? 'rgba(59, 130, 246, 0.3)'
          : 'rgba(59, 130, 246, 0.15)'
      };
    }
  `}
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ToasterLink = styled.a`
  color: ${(props) =>
    props.type === 'success'
      ? props.theme?.colors?.status?.success || '#22C55E'
      : props.type === 'error'
      ? props.theme?.colors?.status?.error || '#EF4444'
      : props.theme?.colors?.status?.info || '#3B82F6'};
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    text-decoration: underline;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ToasterCloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 12px;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }
  background: ${(props) =>
    props.theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.05)'};
  border: 1px solid
    ${(props) =>
      props.theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) =>
    props.theme?.name === 'dark'
      ? props.theme?.colors?.text?.secondaryMuted || '#94A3B8'
      : '#94A3B8'};
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${(props) =>
      props.theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
    color: ${(props) =>
      props.theme?.name === 'dark'
        ? props.theme?.colors?.text?.secondaryMuted || '#94A3B8'
        : '#64748B'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ProgressBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background: ${(props) =>
    props.theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.05)'};
`;

export const ProgressBar = styled.div`
  height: 100%;
  width: 100%;
  transition: width 0.1s linear;

  ${(props) =>
    props.type === 'success' &&
    `
    background: linear-gradient(90deg, #16A34A, #22C55E);
  `}

  ${(props) =>
    props.type === 'error' &&
    `
    background: linear-gradient(90deg, #DC2626, #EF4444);
  `}

  ${(props) =>
    props.type === 'info' &&
    `
    background: linear-gradient(90deg, #2563EB, #3B82F6);
  `}
`;
